import { useTranslation } from 'react-i18next';
import { TriangleAlert, Check, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/shared/components/ui/tooltip';
import type { ScheduleRange, ScheduleChange, Conflict } from '../../types';

interface GanttDragFeedbackProps {
  pendingChange: ScheduleChange | null;
  conflicts: Conflict[];
  onAccept: () => void;
  onReject: () => void;
}

function formatRange(range: ScheduleRange, locale: string): string {
  const fmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
  return `${fmt.format(range.startDate)} – ${fmt.format(range.endDate)}`;
}

/**
 * Inline banner shown only while a drag/resize is pending. Previews the
 * old→new dates and, when the move impacts a dependency, warns and lets the
 * user accept (commit) or reject (revert) before anything persists.
 */
export function GanttDragFeedback({
  pendingChange,
  conflicts,
  onAccept,
  onReject,
}: GanttDragFeedbackProps) {
  const { t, i18n } = useTranslation('tasks');

  if (!pendingChange) return null;

  const hasConflict = conflicts.length > 0;

  return (
    <div
      data-testid="tasks-gantt-drag-feedback"
      className="flex flex-wrap items-center gap-3 border-b border-border bg-muted/50 px-3 py-2 text-sm"
    >
      <span className="text-muted-foreground">
        {t('gantt.drag.moveLabel', {
          from: formatRange(pendingChange.from, i18n.language),
          to: formatRange(pendingChange.to, i18n.language),
        })}
      </span>

      {hasConflict && (
        <span className="flex items-center gap-1.5 font-medium text-foreground">
          <TriangleAlert className="size-4" style={{ color: 'var(--warning)' }} />
          {t('gantt.drag.impactWarning')}
        </span>
      )}

      <div className="ml-auto flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onAccept}
              data-testid="tasks-gantt-drag-accept"
              aria-label={t('gantt.drag.accept')}
            >
              <Check style={{ color: 'var(--success)' }} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('gantt.drag.accept')}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onReject}
              data-testid="tasks-gantt-drag-reject"
              aria-label={t('gantt.drag.reject')}
            >
              <X style={{ color: 'var(--destructive)' }} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('gantt.drag.reject')}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
